/*#
# Markdown interchange

A task travels as a markdown document: a small front-matter block carrying
the id, number, and tags, then the title as a heading and the body. That is
what a card becomes when you drag it to the desktop, and what a dropped
`.md`, text, or HTML turns back into. Plain text with no front matter is
fine — the first line (or first heading) is the title, the rest the body.

```js
import { taskToMarkdown, markdownToTask } from 'tosijs-virta'

const md = taskToMarkdown({ id: 'abc', num: 38, title: 'Ship it', body: 'Soon.', tags: ['status:doing', 'project:me'] })
const back = markdownToTask(md)
preview.textContent = md + '\n---parsed---\n' + JSON.stringify(back)
```
```test
test('round-trips id, number, title, tags, and body', () => {
  expect(preview.textContent).toContain('"id":"abc"')
  expect(preview.textContent).toContain('"num":38')
  expect(preview.textContent).toContain('"title":"Ship it"')
  expect(preview.textContent).toContain('"tags":["status:doing","project:me"]')
  expect(preview.textContent).toContain('"body":"Soon."')
})
```
*/

/*{ "parent": "Reference", "order": 55 }*/

import type { Task } from './model'

export type TaskDoc = Pick<Task, 'id' | 'num' | 'title' | 'body' | 'tags'>

export interface ParsedTask {
  id?: string
  num?: number
  title: string
  body: string
  tags: string[]
}

/** Task → markdown with a `virta` front-matter block. */
export function taskToMarkdown(task: TaskDoc): string {
  const front = [
    '---',
    `virta: ${task.id}`,
    `num: ${task.num}`,
    `tags: [${task.tags.join(', ')}]`,
    '---',
  ].join('\n')
  const body = task.body.trim()
  return `${front}\n# ${task.title}\n${body ? `\n${body}\n` : ''}`
}

/** A safe file name for a task: `38-ship-it.md`. */
export function taskFileName(task: Pick<Task, 'num' | 'title'>): string {
  const slug = task.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
  return `${task.num}${slug ? '-' + slug : ''}.md`
}

/**
 * Markdown (or plain text) → the parts of a task. Front matter is optional
 * and only `virta`, `num`, and `tags` are read from it. The title is the
 * first `#` heading if there is one, else the first non-empty line.
 */
export function markdownToTask(text: string): ParsedTask {
  let src = text.replace(/\r\n?/g, '\n')
  const out: ParsedTask = { title: '', body: '', tags: [] }
  const fm = src.match(/^---\n([\s\S]*?)\n---\n?/)
  if (fm) {
    for (const line of fm[1]!.split('\n')) {
      const m = line.match(/^(\w+):\s*(.*)$/)
      if (!m) continue
      const [, k, v] = m
      if (k === 'virta' && v) out.id = v.trim()
      else if (k === 'num' && /^\d+$/.test(v!.trim())) out.num = Number(v)
      else if (k === 'tags') {
        out.tags = v!
          .replace(/^\[|\]$/g, '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      }
    }
    src = src.slice(fm[0].length)
  }
  const lines = src.split('\n')
  const headingAt = lines.findIndex((l) => /^#{1,6}\s+\S/.test(l))
  const firstText = lines.findIndex((l) => l.trim() !== '')
  const titleAt = headingAt >= 0 ? headingAt : firstText
  if (titleAt < 0) return out
  out.title = lines[titleAt]!.replace(/^#+\s*/, '').trim()
  out.body = lines
    .slice(titleAt + 1)
    .join('\n')
    .trim()
  return out
}

/** Very small HTML → text: block tags become line breaks, the rest is stripped. */
export function htmlToText(html: string): string {
  const doc =
    typeof DOMParser === 'function'
      ? new DOMParser().parseFromString(html, 'text/html')
      : null
  if (!doc) return html.replace(/<[^>]+>/g, '')
  for (const el of Array.from(doc.querySelectorAll('script, style')))
    el.remove()
  for (const el of Array.from(
    doc.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, br, tr')
  )) {
    el.append(doc.createTextNode('\n'))
  }
  for (const h of Array.from(doc.querySelectorAll('h1, h2, h3')))
    h.prepend(doc.createTextNode('# '))
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim()
}
